import React, { useState } from 'react';
import { 
  Table, Card, Button, Space, Typography, Modal, Form, 
  Input, InputNumber, Drawer, message, Descriptions, Radio, Tag,
  Row, Col, DatePicker, Alert, Select, Cascader, Tooltip
} from 'antd';
import { 
  EditOutlined, 
  HistoryOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  InfoCircleOutlined,
  WarningFilled, AccountBookOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

const CreditLimit = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [loading] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isAcctDrawerVisible, setIsAcctDrawerVisible] = useState(false);
  const [isOffsetDrawerVisible, setIsOffsetDrawerVisible] = useState(false);
  const [isOffsetModalVisible, setIsOffsetModalVisible] = useState(false);
  const [offsetForm] = Form.useForm();
  const [currentEnterprise, setCurrentEnterprise] = useState(null);

  // 账户明细筛选：输入态 + 生效态（点击查询后应用）
  const [acctOrderNo, setAcctOrderNo] = useState('');
  const [acctSummary, setAcctSummary] = useState('');
  const [acctType, setAcctType] = useState(null);
  const [acctTimeRange, setAcctTimeRange] = useState(null);
  const [acctQuery, setAcctQuery] = useState({ orderNo: '', summary: '', type: null, timeRange: null });

  // 收支类型 Cascader 选项（三级联动）
  const TRANSACTION_TYPES = [
    {
      value: 'income',
      label: '收入',
      children: [
        { value: 'income_recharge', label: '账户充值' },
        { value: 'income_refund', label: '套餐底价退回' },
        { value: 'income_offset', label: '冲账' },
      ],
    },
    {
      value: 'expense',
      label: '支出',
      children: [
        { value: 'expense_package', label: '套餐底价' },
        { value: 'expense_auth', label: '授权采购' },
        {
          value: 'expense_platform_commission',
          label: '平台抽佣',
          children: [
            { value: 'expense_value_added_commission', label: '增值抽佣' },
            { value: 'expense_ad_commission', label: '广告抽佣' },
          ],
        },
        { value: 'expense_offset', label: '冲账' },
        {
          value: 'expense_private_cloud',
          label: '私有云费用',
          children: [
            { value: 'expense_cloud_host', label: '云主机月账单' },
            { value: 'expense_cdn', label: 'CDN流量' },
            { value: 'expense_oss', label: 'OSS存储' },
            { value: 'expense_bandwidth', label: '动态带宽' },
          ],
        },
        {
          value: 'expense_public_cloud',
          label: '公有云资源',
          children: [
            { value: 'expense_device_traffic', label: '设备流量' },
          ],
        },
      ],
    },
  ];

  // 模拟企业信用额度数据
  const [dataSource, setDataSource] = useState([
    {
      key: '1',
      id: 'HM-8827-X',
      name: '南京鹤梦信息技术有限公司',
      creditLimit: 5000.00,
      balance: 12500.50,
      lastRemark: '项目紧急，申请临时调额',
      operator: 'admin',
      updateTime: '2026-03-20 10:00:00'
    },
    {
      key: '2',
      id: 'JGSf0Nd7',
      name: '东莞康林物联网有限公司',
      creditLimit: 500.00,
      balance: -1200.00, // 可用余额 -700，统计为欠费
      lastRemark: '初始额度',
      operator: 'admin',
      updateTime: '2026-03-15 14:30:00'
    },
    {
      key: '3',
      id: 'A619355x',
      name: '捷顺科技股份有限公司',
      creditLimit: 20000.00,
      balance: 45000.00,
      lastRemark: '年度战略合作伙伴',
      operator: 'HM_finance',
      updateTime: '2026-01-10 09:12:45'
    }
  ]);

  // 计算统计信息
  const totalEnterprises = dataSource.length;
  const overdueEnterprises = dataSource.filter(item => (item.balance + item.creditLimit) < 0).length;

  // 模拟变更记录数据
  const historyData = [
    { key: 'h1', time: '2026-03-20 10:00:00', operator: 'admin', before: 2000.00, change: 3000.00, after: 5000.00, remark: '项目紧急，申请临时调额' },
    { key: 'h2', time: '2026-03-01 09:00:00', operator: 'system', before: 0.00, change: 2000.00, after: 2000.00, remark: '系统自动授信' },
  ];

  // 模拟企业收支明细（type 用于收支类型筛选，summary 为系统自动生成的「交易摘要」）
  const mockTxns = {
    '1': [
      { id: 't1-1', type: ['income', 'income_recharge'], typeLabel: '收入-账户充值', summary: '线下汇款充值', cny: 5000.00, time: '2026-06-20 14:00:00', orderNo: 'REC20260620001' },
      { id: 't1-2', type: ['income', 'income_offset'], typeLabel: '收入-冲账', summary: '平台手动调账（操作人：admin）', cny: 2000.00, time: '2026-06-18 10:00:00', orderNo: 'OFF20260618001' },
      { id: 't1-3', type: ['expense', 'expense_package'], typeLabel: '支出-套餐底价', summary: '套餐「车载流量年包」(PKG100001) 底价扣除（App 自收款）', cny: -800.50, time: '2026-06-15 09:30:00', orderNo: 'PKG20260615001' },
      { id: 't1-4', type: ['expense', 'expense_auth'], typeLabel: '支出-授权采购', summary: '产品「鹤梦云 IoT 模组」授权采购 ×100', cny: -1200.00, time: '2026-06-10 16:00:00', orderNo: 'AUTH20260610001' },
      { id: 't1-5', type: ['expense', 'expense_offset'], typeLabel: '支出-冲账', summary: '平台手动调账（操作人：admin）', cny: -500.00, time: '2026-06-05 11:00:00', orderNo: 'OFF20260605001' },
      { id: 't1-6', type: ['expense', 'expense_private_cloud', 'expense_cloud_host'], typeLabel: '支出-私有云费用-云主机月账单', summary: '云主机 CVM-8827 2026-07 月账单', cny: -1651.61, time: '2026-07-01 10:00:00', orderNo: 'CLOUD-CVM-20260701' },
      { id: 't1-7', type: ['expense', 'expense_private_cloud', 'expense_cdn'], typeLabel: '支出-私有云费用-CDN流量', summary: 'CDN 流量 2026-07 账单', cny: -84.32, time: '2026-07-15 09:00:00', orderNo: 'CLOUD-CDN-20260715' },
      { id: 't1-8', type: ['expense', 'expense_private_cloud', 'expense_oss'], typeLabel: '支出-私有云费用-OSS存储', summary: 'OSS 存储 2026-07 账单', cny: -74.07, time: '2026-07-15 09:00:00', orderNo: 'CLOUD-OSS-20260715' },
      { id: 't1-9', type: ['expense', 'expense_private_cloud', 'expense_bandwidth'], typeLabel: '支出-私有云费用-动态带宽', summary: '动态带宽 2026-07 账单', cny: -45.12, time: '2026-07-19 14:00:00', orderNo: 'CLOUD-BW-20260719' },
      { id: 't1-10', type: ['expense', 'expense_public_cloud', 'expense_device_traffic'], typeLabel: '支出-公有云资源-设备流量', summary: '2026-09 设备流量账单', cny: -1318.53, time: '2026-09-06 08:00:00', orderNo: 'TRAFFIC20260906' },
    ],
    '2': [
      { id: 't2-1', type: ['income', 'income_recharge'], typeLabel: '收入-账户充值', summary: '线下汇款充值', cny: 2000.00, time: '2026-06-22 09:00:00', orderNo: 'REC20260622001' },
      { id: 't2-2', type: ['expense', 'expense_package'], typeLabel: '支出-套餐底价', summary: '套餐「停车云基础版」(PKG100002) 底价扣除（App 自收款）', cny: -1800.00, time: '2026-06-20 15:00:00', orderNo: 'PKG20260620001' },
      { id: 't2-3', type: ['expense', 'expense_platform_commission', 'expense_value_added_commission'], typeLabel: '支出-平台抽佣-增值抽佣', summary: '2026-06 增值抽佣', cny: -350.00, time: '2026-06-18 12:00:00', orderNo: 'COM20260618001' },
    ],
    '3': [
      { id: 't3-1', type: ['income', 'income_recharge'], typeLabel: '收入-账户充值', summary: '支付宝在线充值', cny: 10000.00, time: '2026-06-21 10:00:00', orderNo: 'REC20260621001' },
      { id: 't3-2', type: ['income', 'income_offset'], typeLabel: '收入-冲账', summary: '平台手动调账（操作人：admin）', cny: 5000.00, time: '2026-06-19 08:00:00', orderNo: 'OFF20260619001' },
      { id: 't3-3', type: ['expense', 'expense_auth'], typeLabel: '支出-授权采购', summary: '产品「鹤梦云 IoT 模组」授权采购 ×500', cny: -3500.00, time: '2026-06-15 14:00:00', orderNo: 'AUTH20260615001' },
      { id: 't3-4', type: ['expense', 'expense_platform_commission', 'expense_value_added_commission'], typeLabel: '支出-平台抽佣-增值抽佣', summary: '2026-06 增值抽佣', cny: -1200.00, time: '2026-06-10 09:00:00', orderNo: 'COM20260610001' },
    ],
  };

  // 处理修改弹窗
  const handleEdit = (record) => {
    setCurrentEnterprise(record);
    editForm.setFieldsValue({
      creditLimit: record.creditLimit,
      remark: ''
    });
    setIsEditModalVisible(true);
  };

  // 保存修改
  const handleSave = async () => {
    try {
      const values = await editForm.validateFields();
      const newData = dataSource.map(item => {
        if (item.key === currentEnterprise.key) {
          return {
            ...item,
            creditLimit: values.creditLimit,
            lastRemark: values.remark,
            updateTime: new Date().toLocaleString()
          };
        }
        return item;
      });
      setDataSource(newData);
      message.success(`${currentEnterprise.name} 的信用配额已更新`);
      setIsEditModalVisible(false);
    } catch (error) {
      console.log('Validate Failed:', error);
    }
  };

  const columns = [
    { title: '序号', dataIndex: 'key', key: 'key', width: 70, align: 'center' },
    { 
      title: '企业信息', 
      key: 'enterprise',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, color: '#262626' }}>{record.name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>ID: {record.id}</Text>
        </div>
      )
    },
    { 
      title: '信用配额', 
      dataIndex: 'creditLimit', 
      key: 'creditLimit',
      render: (val, record) => (
        <Space>
          <Text strong>¥ {val.toFixed(2)}</Text>
          <Button 
            type="link" 
            size="small" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            修改
          </Button>
        </Space>
      )
    },
    { 
      title: '备注信息', 
      dataIndex: 'lastRemark', 
      key: 'lastRemark',
      width: 250,
      render: (text) => <Text type="secondary" style={{ fontSize: 13 }}>{text || '-'}</Text>
    },
    { 
      title: '账户余额', 
      dataIndex: 'balance', 
      key: 'balance',
      align: 'right',
      render: (val) => <Text style={{ color: val < 0 ? '#ff4d4f' : 'inherit' }}>¥ {val.toFixed(2)}</Text>
    },
    { 
      title: '可用余额', 
      key: 'available',
      align: 'right',
      render: (_, record) => {
        const available = record.balance + record.creditLimit;
        return (
          <Text strong style={{ color: available < 0 ? '#ff4d4f' : '#52c41a' }}>
            ¥ {available.toFixed(2)}
          </Text>
        );
      }
    },
    { 
      title: '操作', 
      key: 'action',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <>
        <Button 
          type="link" 
          icon={<HistoryOutlined />} 
          onClick={() => {
            setCurrentEnterprise(record);
            setIsDrawerVisible(true);
          }}
        >
          变更记录
        </Button>
        <Button
          type="link"
          icon={<AccountBookOutlined />}
          onClick={() => {
            setCurrentEnterprise(record);
            setAcctOrderNo('');
            setAcctSummary('');
            setAcctType(null);
            setAcctTimeRange(null);
            setAcctQuery({ orderNo: '', summary: '', type: null, timeRange: null });
            setIsAcctDrawerVisible(true);
          }}
        >
          账户明细
        </Button>
        </>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>企业信用额度管理</Title>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item>
            <Space.Compact>
              <Form.Item name="searchType" initialValue="name" noStyle>
                <Select style={{ width: 100 }}>
                  <Select.Option value="name">企业名称</Select.Option>
                  <Select.Option value="id">企业ID</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate={(prev, curr) => prev.searchType !== curr.searchType} noStyle>
                {({ getFieldValue }) => (
                  <Form.Item name="searchValue" noStyle>
                    <Input 
                      placeholder={getFieldValue('searchType') === 'id' ? "请输入企业ID" : "请输入企业名称"} 
                      allowClear 
                      style={{ width: 200 }} 
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Alert
        message={
          <div style={{ padding: '4px 0' }}>
            <div style={{ marginBottom: 4 }}>
              共 <Text strong style={{ color: '#1890ff', fontSize: '15px' }}>{totalEnterprises}</Text> 家企业，
              <Text type="danger" strong style={{ fontSize: '15px' }}>{overdueEnterprises}</Text> 家存在欠费。
            </div>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              <WarningFilled style={{ color: '#ff4d4f', marginRight: 6 }} />
              已欠费状态的企业，将关停其云平台资源（包括但不限于鹤梦云生态平台服务、APP服务等）。
            </Text>
          </div>
        }
        type="warning"
        showIcon
        style={{ marginBottom: 16, borderRadius: 8, border: '1px solid #ffe58f', background: '#fffbe6' }}
      />

      <Card bodyStyle={{ padding: 0 }}>
        <Table 
          columns={columns} 
          dataSource={dataSource} 
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条企业记录`
          }}
        />
      </Card>

      {/* 修改额度弹窗 */}
      <Modal
        title="修改企业信用配额"
        open={isEditModalVisible}
        onOk={handleSave}
        onCancel={() => setIsEditModalVisible(false)}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        {currentEnterprise && (
          <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="企业名称"><Input value={currentEnterprise.name} disabled /></Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="企业ID"><Input value={currentEnterprise.id} disabled /></Form.Item>
              </Col>
            </Row>
            <Form.Item 
              name="creditLimit" 
              label="信用配额 (¥)" 
              required
              rules={[{ required: true, message: '请输入信用配额' }]}
              extra={
                <Space style={{ color: '#faad14', marginTop: 4 }}>
                  <InfoCircleOutlined />
                  <span>企业最高可欠费额度</span>
                </Space>
              }
            >
              <InputNumber 
                style={{ width: '100%' }} 
                precision={2} 
                min={0} 
                placeholder="请输入额度"
              />
            </Form.Item>
            <Form.Item 
              name="remark" 
              label="备注说明" 
              rules={[{ required: true, message: '请输入修改备注' }]}
            >
              <Input.TextArea rows={3} placeholder="请填写本次额度变更的原因（必填）" maxLength={200} showCount />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* 变更记录抽屉 */}
      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            <span>额度变更记录 - {currentEnterprise?.name}</span>
          </Space>
        }
        width={900}
        onClose={() => setIsDrawerVisible(false)}
        open={isDrawerVisible}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Form layout="inline">
            <Form.Item label="操作时间">
              <DatePicker.RangePicker 
                showTime 
                placeholder={['开始时间', '结束时间']} 
                style={{ width: 380 }}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" icon={<SearchOutlined />}>查询</Button>
                <Button icon={<ReloadOutlined />}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
        <Table
          size="small"
          dataSource={historyData}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: '操作时间', dataIndex: 'time', key: 'time'},
            { title: '操作账号', dataIndex: 'operator', key: 'operator' },
            { 
              title: '信用额度', 
              dataIndex: 'before', 
              key: 'before',
              render: (val) => `¥${val.toFixed(2)}`
            },
            { 
              title: '备注信息', 
              dataIndex: 'remark', 
              key: 'remark',
              // width: 150, 
              render: (text) => <Text type="secondary">{text}</Text>
            }
          ]}
        />
      </Drawer>

      {/* 账户明细抽屉 */}
      <Drawer
        title={<Space><AccountBookOutlined /><span>账户明细 - {currentEnterprise?.name}</span></Space>}
        open={isAcctDrawerVisible} onClose={() => setIsAcctDrawerVisible(false)} width={900} destroyOnClose
      >
        {currentEnterprise && (() => {
          const txns = mockTxns[currentEnterprise.key] || [];
          const sorted = [...txns].sort((a, b) => new Date(a.time) - new Date(b.time));
          let running = currentEnterprise.balance - sorted.reduce((s, t) => s + t.cny, 0);
          const balMap = {};
          sorted.forEach(t => { running += t.cny; balMap[t.id] = parseFloat(running.toFixed(2)); });

          // 按生效的筛选条件过滤：业务单号与交易摘要分别独立匹配
          const { orderNo, summary, type, timeRange } = acctQuery;
          const filteredTxns = txns.filter((t) => {
            if (orderNo && !(t.orderNo || '').toLowerCase().includes(orderNo.toLowerCase())) return false;
            if (summary && !(t.summary || '').toLowerCase().includes(summary.toLowerCase())) return false;
            if (type && type.length) {
              const matched = type.some((path) => t.type && path.every((seg, i) => t.type[i] === seg));
              if (!matched) return false;
            }
            if (timeRange && timeRange.length === 2) {
              const ts = new Date(t.time).getTime();
              if (ts < timeRange[0].valueOf() || ts > timeRange[1].valueOf()) return false;
            }
            return true;
          });

          return (
            <div>
              <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="企业名称">{currentEnterprise.name}</Descriptions.Item>
                <Descriptions.Item label="企业ID">{currentEnterprise.id}</Descriptions.Item>
                <Descriptions.Item label="账户余额">
                  <Text strong style={{ fontSize: 16, color: currentEnterprise.balance < 0 ? '#ff4d4f' : '#52c41a' }}>
                    ¥ {currentEnterprise.balance.toFixed(2)}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="信用配额">
                  <Text strong style={{ fontSize: 16 }}>¥ {currentEnterprise.creditLimit.toFixed(2)}</Text>
                </Descriptions.Item>
              </Descriptions>

              <Card title="收支明细" size="small"
                extra={
                  <Space>
                    <Button icon={<SearchOutlined />}>导出</Button>
                    <Button type="primary" icon={<AccountBookOutlined />}
                      onClick={() => setIsOffsetDrawerVisible(true)}>冲账</Button>
                  </Space>
                }>
                <div style={{ marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Input
                    placeholder="业务单号"
                    allowClear
                    value={acctOrderNo}
                    onChange={(e) => setAcctOrderNo(e.target.value)}
                    style={{ width: 160 }}
                  />
                  <Input
                    placeholder="交易摘要"
                    allowClear
                    value={acctSummary}
                    onChange={(e) => setAcctSummary(e.target.value)}
                    style={{ width: 180 }}
                  />
                  <Cascader
                    placeholder="收支类型"
                    options={TRANSACTION_TYPES}
                    multiple
                    showCheckedStrategy={Cascader.SHOW_CHILD}
                    maxTagCount="responsive"
                    style={{ width: 240 }}
                    allowClear
                    value={acctType}
                    onChange={setAcctType}
                  />
                  <DatePicker.RangePicker
                    style={{ width: 260 }}
                    placeholder={['开始时间', '结束时间']}
                    value={acctTimeRange}
                    onChange={setAcctTimeRange}
                  />
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />}
                      onClick={() => setAcctQuery({
                        orderNo: acctOrderNo.trim(),
                        summary: acctSummary.trim(),
                        type: acctType && acctType.length ? acctType : null,
                        timeRange: acctTimeRange || null,
                      })}>查询</Button>
                    <Button icon={<ReloadOutlined />}
                      onClick={() => {
                        setAcctOrderNo('');
                        setAcctSummary('');
                        setAcctType(null);
                        setAcctTimeRange(null);
                        setAcctQuery({ orderNo: '', summary: '', type: null, timeRange: null });
                      }}>重置</Button>
                  </Space>
                </div>
                <Table dataSource={[...filteredTxns].sort((a, b) => new Date(b.time) - new Date(a.time))} size="small" rowKey="id"
                  pagination={{ pageSize: 10, showTotal: t => '共 ' + t + ' 条', showSizeChanger: true }}
                  locale={{ emptyText: '暂无收支记录' }}
                  columns={[
                    { title: '收支类型', dataIndex: 'typeLabel', render: v => <Tag color={v.startsWith('收入') ? 'success' : 'error'} style={{ border: 'none' }}>{v}</Tag> },
                    { title: '交易摘要', dataIndex: 'summary', width: 220, ellipsis: { showTitle: false }, render: v => <Tooltip title={v} placement="topLeft">{v}</Tooltip> },
                    { title: '金额 CN¥', dataIndex: 'cny', align: 'right', render: v => <Text strong style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f' }}>{v >= 0 ? '+' + v.toFixed(2) : v.toFixed(2)}</Text> },
                    { title: '账户余额', key: 'bal', align: 'right', render: (_, r) => { const b = balMap[r.id]; return <Text strong style={{ color: b >= 0 ? '#52c41a' : '#ff4d4f' }}>¥ {b.toFixed(2)}</Text>; } },
                    { title: '时间', dataIndex: 'time' },
                    { title: '单号', dataIndex: 'orderNo', render: v => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
                  ]} />
              </Card>
            </div>
          );
        })()}
      </Drawer>

      {/* 冲账管理子抽屉 */}
      <Drawer
        title={<Space><AccountBookOutlined /><span>冲账管理 - {currentEnterprise?.name}</span></Space>}
        open={isOffsetDrawerVisible} onClose={() => setIsOffsetDrawerVisible(false)} width={700}
      >
        {currentEnterprise && (() => {
          const offsetRecords = (mockTxns[currentEnterprise.key] || []).filter(t => t.typeLabel.includes('冲账'));

          const handleOffsetSubmit = async () => {
            try {
              const vals = await offsetForm.validateFields();
              const isPos = offsetForm.getFieldValue('direction') !== 'negative';
              const absAmt = Math.abs(vals.offsetAmount);
              const amt = isPos ? absAmt : -absAmt;
              const label = isPos ? '收入-冲账' : '支出-冲账';
              const txn = {
                id: 'off-' + Date.now(),
                type: isPos ? ['income', 'income_offset'] : ['expense', 'expense_offset'],
                typeLabel: label,
                summary: vals.offsetReason,
                cny: amt,
                time: new Date().toLocaleString('zh-CN', { hour12: false }),
                orderNo: 'OFF' + Date.now(),
              };
              mockTxns[currentEnterprise.key] = [...(mockTxns[currentEnterprise.key] || []), txn];
              setDataSource(prev => prev.map(item => item.key === currentEnterprise.key ? { ...item, balance: parseFloat((item.balance + amt).toFixed(2)) } : item));
              setCurrentEnterprise(prev => ({ ...prev, balance: parseFloat((prev.balance + amt).toFixed(2)) }));
              message.success('冲账成功');
              setIsOffsetModalVisible(false);
              offsetForm.resetFields();
            } catch (e) {}
          };

          return (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<AccountBookOutlined />}
                  onClick={() => setIsOffsetModalVisible(true)}>发起冲账</Button>
              </div>
              <Table dataSource={offsetRecords} size="small" rowKey="id"
                locale={{ emptyText: '暂无冲账记录' }}
                columns={[
                  { title: '时间', dataIndex: 'time' },
                  { title: '方向', dataIndex: 'cny', render: v => <Tag color={v >= 0 ? 'success' : 'error'} style={{ border: 'none' }}>{v >= 0 ? '冲入' : '冲入'}</Tag> },
                  { title: '金额', dataIndex: 'cny', align: 'right', render: v => <Text strong style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f' }}>{v >= 0 ? '+' + v.toFixed(2) : v.toFixed(2)}</Text> },
                  { title: '原因', dataIndex: 'orderNo', render: () => <Text>平台手动调账</Text> },
                  { title: '操作人', render: () => <Text>admin</Text> },
                ]} />

              <Modal title="发起冲账" open={isOffsetModalVisible}
                onCancel={() => setIsOffsetModalVisible(false)} onOk={handleOffsetSubmit}
                okText="确认提交" destroyOnClose
              >
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary">企业：{currentEnterprise.name}</Text>
                  <br />
                  <Text type="secondary">当前余额：¥ {currentEnterprise.balance.toFixed(2)}</Text>
                </div>
                <Form form={offsetForm} layout="vertical">
                  <Form.Item label="冲账方向" name="direction" initialValue="positive">
                    <Radio.Group>
                      <Radio value="positive" style={{ color: '#52c41a' }}>+ 冲入正数（增加余额）</Radio>
                      <Radio value="negative" style={{ color: '#ff4d4f' }}>− 冲入负数（扣减余额）</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item name="offsetAmount" label="金额" rules={[{ required: true, message: '请输入金额' }]}>
                    <InputNumber min={0.01} precision={2} style={{ width: '100%' }} prefix="¥" placeholder="请输入金额" />
                  </Form.Item>
                  <Form.Item name="offsetReason" label="冲账原因" rules={[{ required: true, message: '请填写冲账原因' }]}>
                    <Input.TextArea rows={3} placeholder="请填写冲账原因（必填）" />
                  </Form.Item>
                </Form>
              </Modal>
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
};

export default CreditLimit;
